import React, { useEffect, useState } from 'react';
import _ from 'lodash';

import { NewAnomalyItem as AnomalyItem, AnomalySummary as AnomalySummaryModel, ChecklistItem, FVIPDOChecklistItem, FVIPipingItem, PDFImageProps, PIDData } from '../../../../utils/models';
import { getUpdatedPipingAnomalySummary, matchPhrases } from '../../../../utils/utilities';
import { PIPING_SUMMARY_KEY } from '../../../../utils/constants';

import AccordionItem from '../accordion-item';
import AnomalyLocations from '../../../forms/custom-fvi/anomalyLocations';
import Conditional from '../../../common/conditional';
import Confirm from '../../../themed/confirm';
import LineNumberForm from '../../../forms/custom-fvi/lineNumberForm';
import PIDImageForm from '../../../forms/custom-fvi/pidImageForm';
import PipingOverview from '../piping-overview';
import VIPhotoRecordForm from '../../../forms/visual-inspection/viPhotoRecordForm';

import classNames from './style.module.scss';

interface ExistingPipingItemProps {
	nextAnomalyID: number;
	pipingItem: FVIPipingItem;
	showLastLineOption: boolean;
	isNewFormat: boolean;
	onDeletePipingItem: (lineNumber: string) => void;
	onEditPipingItem: (initialPipingItem: FVIPipingItem, updatedPipingItem: Partial<FVIPipingItem>) => void;
	onSaveAnomalySummary: (lineNumber: string, anomalySummary: AnomalySummaryModel) => void;
	onChangePID: (lineNumber: string, pIDData: Pick<FVIPipingItem, 'pIDImages' | 'pIDNumber'>) => void;
	onChangeComment: (lineNumber: string, comment: string) => void;
	onChangePipingChecklist: (lineNumber: string, items: ChecklistItem[]) => void;
	onChangePipingAnomaly: (lineNumber: string, items: AnomalyItem[]) => void;
	onChangeGeneralPhotos: (lineNumber: string, images: PDFImageProps[]) => void;
	onChangePDOChecklist: (lineNumber: string, items: FVIPDOChecklistItem[]) => void;
	onChangePipingAnomalyLocation: (lineNumber: string, images: PDFImageProps[]) => void;
}

const ExistingPipingItem: React.FC<ExistingPipingItemProps> = ({
	nextAnomalyID,
	pipingItem,
	showLastLineOption,
	isNewFormat,
	onEditPipingItem,
	onDeletePipingItem,
	onChangePDOChecklist,
	onChangePID,
	onChangeComment,
	onChangePipingChecklist,
	onChangePipingAnomaly,
	onChangePipingAnomalyLocation,
	onChangeGeneralPhotos,
	onSaveAnomalySummary,
}) => {
	const [deleteLineItem, setDeleteLineItem] = useState(false);
	const [editLineItem, setEditLineItem] = useState(false);
	const [isOpen, setOpen] = useState(false);

	const handleConfirmDeletion = () => {
		onDeletePipingItem(pipingItem.lineNumber);
	};

	const handleEditPipingItem = (newPipingLineDetails: Partial<FVIPipingItem>) => {
		onEditPipingItem(pipingItem, newPipingLineDetails);
		setEditLineItem(false);
		setOpen(true);
	};

	const handleToggleDeletion = () => {
		setDeleteLineItem((previous) => !previous);
	};
	
	const handleToggleEdit = () => {
		setEditLineItem((previous) => !previous);
	};

	useEffect(() => {
		function updateAnomalySummary() {
			const newAnomalySummary = getUpdatedPipingAnomalySummary(isNewFormat, pipingItem.anomalySummary, pipingItem.anomalyItems);

			if (_.isEqual(pipingItem.anomalySummary, newAnomalySummary)) {
				return;
			}
			
			if (newAnomalySummary) {
				onSaveAnomalySummary(pipingItem.lineNumber, newAnomalySummary)
			};
		}

		updateAnomalySummary();
	}, [isNewFormat, pipingItem.lineNumber, pipingItem.anomalySummary, pipingItem.anomalyItems]);

	return (
		<>
			<Conditional isVisible={deleteLineItem}>
				<Confirm
					title={'Delete Line Number ' + pipingItem.lineNumber + '?'}
					message="Are you sure you want to delete this item? This action is not reversible!"
					onCancel={handleToggleDeletion}
					onConfirm={handleConfirmDeletion}
				/>
			</Conditional>

			<Conditional isVisible={editLineItem}>
				<div className="bg-white round border p-3">
					<LineNumberForm
						lineDetails={_.pick(pipingItem, ['lineNumber', 'hasPDO', 'hasPID', 'hasAnomalies', 'inspectionDescription'])} 
						onAdd={handleEditPipingItem}
						onCancelEdit={handleToggleEdit}
						showLastLineOption={showLastLineOption}
					/>
				</div>
			</Conditional>
			
			<Conditional isVisible={!deleteLineItem && !editLineItem}>
				<div className={classNames.accordionItem}>
					<div
						className={classNames.accordionItemHeader}
						onClick={() => setOpen((prev) => !prev)}
						role="button"
					>
						<div>Line Number: {pipingItem.lineNumber}</div>

						<div className="d-flex justify-content-center align-items-center">
							<button className={classNames.accordionItemEdit} onClick={handleToggleEdit}>
								<i className="bi bi-pencil"></i>
							</button>

							<button className={classNames.accordionItemDelete} onClick={handleToggleDeletion}>
								<i className="bi bi-trash"></i>
							</button>

							<div className={classNames.accordionItemIcon}>
								<i className={`bi bi-chevron-${isOpen ? 'down' : 'right'}`}></i>
							</div>
						</div>
					</div>


					<Conditional isVisible={isOpen}>
						<div className={classNames.accordionItemBody}>
							<Conditional isVisible={matchPhrases(pipingItem.hasPID, 'yes')}>
								<AccordionItem isActive title="P&ID">
									<PIDImageForm
										initialPIDData={_.pick(pipingItem, ['pIDNumber', 'pIDImages'])}
										onAdd={(pIDData: Omit<PIDData, 'hasPID'>) => onChangePID(pipingItem.lineNumber, pIDData)}
									/>
								</AccordionItem>
							</Conditional>

							<PipingOverview
								lineId={pipingItem.id}
								anomalyItems={pipingItem.anomalyItems}
								lineNumber={pipingItem.lineNumber}
								additionalComment={pipingItem.checklistAdditionalComment}
								anomalySummary={pipingItem.anomalySummary}
								checklistItems={pipingItem.checklistItems}
								hasAnomalies={pipingItem.hasAnomalies}
								nextAnomalyID={nextAnomalyID}
								isNewFormat={isNewFormat}
								isPDOFormat={matchPhrases(pipingItem.hasPDO, 'yes')}
								isChecklistVisible
								pdoChecklistItems={pipingItem.pdoChecklist}
								onChangePDOChecklistItems={(items) => onChangePDOChecklist(pipingItem.lineNumber, items)}
								onChangeAnomalies={(items) => onChangePipingAnomaly(pipingItem.lineNumber, items)}
								onChangeSummary={(summary) => onSaveAnomalySummary(pipingItem.lineNumber, summary)}
								onChangeAdditionalComment={(comment) => onChangeComment(pipingItem.lineNumber, comment)}
								onChangeChecklistItems={(items) => onChangePipingChecklist(pipingItem.lineNumber, items)}
							/>

							<AccordionItem isActive title="Anomaly Locations">
								<AnomalyLocations
									initialAnomalyLocations={pipingItem.anomalyLocations}
									onAdd={(images: PDFImageProps[]) => onChangePipingAnomalyLocation(pipingItem.lineNumber, images)}
								/>
							</AccordionItem>

							<Conditional isVisible={true}>
								<AccordionItem isActive title="General Photos (Optional)">
									<VIPhotoRecordForm
										initialGeneralPhotos={pipingItem.generalPhotos}
										onProceed={(images: PDFImageProps[]) => onChangeGeneralPhotos(pipingItem.lineNumber, images)}
									/>
								</AccordionItem>
							</Conditional>
						</div>
					</Conditional>
				</div>
			</Conditional>
		</>
	);
};

export const PIPING_LOCATION_OPTIONS = [
	{
		label: PIPING_SUMMARY_KEY.PIPE_BODY,
		value: PIPING_SUMMARY_KEY.PIPE_BODY
	},
	{
		label: PIPING_SUMMARY_KEY.FLANGE,
		value: PIPING_SUMMARY_KEY.FLANGE,
	},
	{
		label: PIPING_SUMMARY_KEY.VALVE,
		value: PIPING_SUMMARY_KEY.VALVE,
	},
	{
		label: PIPING_SUMMARY_KEY.UNDER_PIPE_SUPPORT,
		value: PIPING_SUMMARY_KEY.UNDER_PIPE_SUPPORT,
	},
	{
		label: PIPING_SUMMARY_KEY.PIPE_SUPPORT,
		value: PIPING_SUMMARY_KEY.PIPE_SUPPORT,
	},
	{
		label: PIPING_SUMMARY_KEY.SMALL_BORE_PIPE,
		value: PIPING_SUMMARY_KEY.SMALL_BORE_PIPE,
	},
	{
		label: PIPING_SUMMARY_KEY.DRAIN_BOX,
		value: PIPING_SUMMARY_KEY.DRAIN_BOX,
	},
];

export default ExistingPipingItem;
